import { useEffect, useState } from 'react'
import { InlineProperty, PropertySection, StaticProperty } from '@/components/patterns/PropertySection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { toast } from '@/lib/toast'
import { listContacts } from '../api'
import { CategorySelect } from '../components/CategorySelect'
import { amsterdamDay, lastActivityText, shortDay } from '../record/format'
import { SOURCE_LABELS } from '../record/labels'
import type {
  ConsentChannel,
  ConsentStatus,
  CrmContactDetail,
  CrmContactListItem,
  CrmContactUpdate,
} from '../types'
import { putConsent } from './api'

const selectClass =
  'h-8 rounded-md border border-border bg-background px-2 text-sm max-md:h-11 max-md:text-base'

const CONSENT_CHANNEL_LABELS: Record<ConsentChannel, string> = {
  newsletter: 'Nieuwsbrief',
  events: 'Events',
}

const CONSENT_STATUS_LABELS: Record<ConsentStatus, string> = {
  opt_in: 'Opt-in',
  opt_out: 'Opt-uit',
  unknown: 'Onbekend',
}

/** "1 mrt 2025 · Jesse", or a dash when the contact has no such moment. */
function momentText(moment: string | null, by?: string): string {
  if (!moment) return '—'
  const day = shortDay(amsterdamDay(moment))
  return by ? `${day} · ${by}` : day
}

/**
 * The property sections in the left column of the contact page (design §4.5): Over dit
 * contact, Relatie, Mailvoorkeuren (AVG) and Systeem. `onPatch` lets an error through (the
 * inline fields show it); `onPatchWithToast` reports it (pickers and selects); `onDetail` takes
 * over an answer that carries the whole contact (a consent).
 */
export function ContactProperties({
  detail,
  onPatch,
  onPatchWithToast,
  onDetail,
  onOwnerChange,
}: {
  detail: CrmContactDetail
  onPatch: (fields: CrmContactUpdate) => Promise<void>
  onPatchWithToast: (fields: CrmContactUpdate, success?: string) => Promise<void>
  onDetail: (detail: CrmContactDetail) => void
  /** The owner select: the page asks about handing over agreements and tasks (ED-33). */
  onOwnerChange: (ownerId: string | null) => void
}) {
  const { membersById } = useTeamMembers()
  const team = membersById
    ? [...membersById.values()].sort((a, b) => a.display_name.localeCompare(b.display_name, 'nl'))
    : []

  return (
    <>
      <PropertySection title="Over dit contact" storageKey="contact-over">
        <InlineProperty
          label="Naam"
          value={detail.full_name}
          required
          onSave={(value) => onPatch({ full_name: value ?? detail.full_name })}
        />
        <InlineProperty
          label="E-mail"
          type="email"
          value={detail.email}
          onSave={(value) => onPatch({ email: value })}
        />
        <InlineProperty
          label="Telefoon"
          type="tel"
          value={detail.phone}
          onSave={(value) => onPatch({ phone: value })}
        />
        <InlineProperty
          label="Functie"
          value={detail.job_title}
          onSave={(value) => onPatch({ job_title: value })}
        />
        <InlineProperty
          label="LinkedIn"
          type="url"
          value={detail.linkedin_url}
          onSave={(value) => onPatch({ linkedin_url: value })}
        />
      </PropertySection>
      <PropertySection title="Relatie" storageKey="contact-relatie">
        <StaticProperty label="Categorie">
          <CategorySelect
            value={detail.category}
            onChange={(category) => void onPatchWithToast({ category_id: category?.id ?? null })}
          />
        </StaticProperty>
        <StaticProperty label="Eigenaar">
          <select
            aria-label="Eigenaar"
            value={detail.owner?.id ?? ''}
            onChange={(event) => onOwnerChange(event.target.value || null)}
            className={`${selectClass} w-full`}
          >
            <option value="">Geen eigenaar</option>
            {detail.owner && !membersById?.has(detail.owner.id) ? (
              <option value={detail.owner.id}>{detail.owner.full_name}</option>
            ) : null}
            {team.map((member) => (
              <option key={member.id} value={member.id}>
                {member.display_name}
              </option>
            ))}
          </select>
        </StaticProperty>
        <StaticProperty label="Status">
          <select
            aria-label="Status"
            value={detail.status}
            onChange={(event) =>
              void onPatchWithToast({ status: event.target.value === 'inactief' ? 'inactief' : 'actief' })
            }
            className={`${selectClass} w-full`}
          >
            <option value="actief">Actief</option>
            <option value="inactief">Inactief</option>
          </select>
        </StaticProperty>
        <InlineProperty
          label="Relatie sinds"
          type="date"
          value={detail.relation_since}
          onSave={(value) => onPatch({ relation_since: value })}
        />
        <IntroducedByProperty detail={detail} onPatchWithToast={onPatchWithToast} />
        <StaticProperty label="Bron">{SOURCE_LABELS[detail.source] ?? detail.source}</StaticProperty>
      </PropertySection>
      <PropertySection title="Mailvoorkeuren (AVG)" storageKey="contact-avg" defaultOpen={false}>
        {(Object.keys(CONSENT_CHANNEL_LABELS) as ConsentChannel[]).map((channel) => (
          <ConsentRow key={channel} channel={channel} detail={detail} onDetail={onDetail} />
        ))}
      </PropertySection>
      <PropertySection title="Systeem" storageKey="contact-systeem" defaultOpen={false}>
        <StaticProperty label="Aangemaakt">
          {momentText(detail.created_at, detail.created_by?.full_name)}
        </StaticProperty>
        <StaticProperty label="Gewijzigd">{momentText(detail.updated_at)}</StaticProperty>
        {detail.last_activity ? (
          <StaticProperty label="Laatste activiteit">{lastActivityText(detail.last_activity)}</StaticProperty>
        ) : null}
      </PropertySection>
    </>
  )
}

/**
 * "Geïntroduceerd door": search a contact from two characters (`listContacts({ q })`), pick
 * one, or clear the link.
 */
function IntroducedByProperty({
  detail,
  onPatchWithToast,
}: {
  detail: CrmContactDetail
  onPatchWithToast: (fields: CrmContactUpdate) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<CrmContactListItem[]>([])

  useEffect(() => {
    let cancelled = false
    const timer = setTimeout(() => {
      if (!editing || q.trim().length < 2) {
        if (!cancelled) setResults([])
        return
      }
      listContacts({ q: q.trim() })
        .then((res) => {
          if (!cancelled) {
            setResults(res.items.filter((row) => row.person_id !== detail.person_id).slice(0, 6))
          }
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [editing, q, detail.person_id])

  async function pick(personId: string | null) {
    setEditing(false)
    setQ('')
    await onPatchWithToast({ introduced_by_person_id: personId })
  }

  return (
    <StaticProperty label="Geïntroduceerd door">
      {editing ? (
        <div className="flex flex-col gap-1">
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setEditing(false)
            }}
            placeholder="Zoek een contact…"
            aria-label="Zoek introducerend contact"
            autoFocus
            className="max-md:h-11"
          />
          {results.length > 0 ? (
            <div className="flex flex-col rounded-lg border border-border bg-background shadow-sm">
              {results.map((row) => (
                <button
                  key={row.person_id}
                  type="button"
                  onClick={() => void pick(row.person_id)}
                  className="px-3 py-1.5 text-left text-sm hover:bg-muted max-md:min-h-11"
                >
                  {row.full_name}
                  {row.organisation ? (
                    <span className="text-muted-foreground"> — {row.organisation}</span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {detail.introduced_by ? (
              <Button type="button" variant="outline" size="sm" onClick={() => void pick(null)}>
                Koppeling wissen
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Annuleren
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label="Geïntroduceerd door bewerken"
          className="min-w-0 rounded px-1 text-left text-sm hover:bg-muted max-md:min-h-11"
        >
          {detail.introduced_by ? (
            detail.introduced_by.full_name
          ) : (
            <span className="text-muted-foreground">Toevoegen…</span>
          )}
        </button>
      )}
    </StaticProperty>
  )
}

/** The mail preference of one channel (AVG): a status and where it came from, saved through `putConsent`. */
function ConsentRow({
  channel,
  detail,
  onDetail,
}: {
  channel: ConsentChannel
  detail: CrmContactDetail
  onDetail: (detail: CrmContactDetail) => void
}) {
  const consent = detail.consents.find((entry) => entry.channel === channel)
  const status: ConsentStatus = consent?.status ?? 'unknown'
  const [source, setSource] = useState(consent?.source ?? '')
  const [lastSource, setLastSource] = useState(consent?.source ?? '')

  // The stored value changed (a reload)? Reset the field while rendering — the "adjusting
  // state when a prop changes" pattern, without an effect.
  if ((consent?.source ?? '') !== lastSource) {
    setLastSource(consent?.source ?? '')
    setSource(consent?.source ?? '')
  }

  async function save(nextStatus: ConsentStatus, nextSource: string) {
    try {
      onDetail(
        await putConsent(detail.person_id, {
          channel,
          status: nextStatus,
          source: nextSource.trim() || undefined,
        }),
      )
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Voorkeur opslaan mislukt.')
    }
  }

  return (
    <StaticProperty label={CONSENT_CHANNEL_LABELS[channel]}>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(event) => void save(event.target.value as ConsentStatus, source)}
          aria-label={`Voorkeur ${CONSENT_CHANNEL_LABELS[channel]}`}
          className={selectClass}
        >
          {(Object.keys(CONSENT_STATUS_LABELS) as ConsentStatus[]).map((value) => (
            <option key={value} value={value}>
              {CONSENT_STATUS_LABELS[value]}
            </option>
          ))}
        </select>
        <Input
          value={source}
          onChange={(event) => setSource(event.target.value)}
          onBlur={() => {
            if ((consent?.source ?? '') !== source.trim()) void save(status, source)
          }}
          placeholder="Bron (bijv. event)"
          aria-label={`Bron ${CONSENT_CHANNEL_LABELS[channel]}`}
          className="min-w-24 flex-1 max-md:h-11"
        />
        {status === 'opt_out' && consent?.source === 'mailchimp' && (
          <Badge variant="outline">via Mailchimp</Badge>
        )}
      </div>
    </StaticProperty>
  )
}
